import { useCallback } from 'react';

import { GetContextMenuItems, MenuItemDef } from 'ag-grid-community';

import type { ShipmentEmbryo } from '@/services/shipping/types';
import {
  auditHistoryMenuItem,
  DEFAULT_CONTEXT_MENU,
  replaceExportItem,
} from '@/utils/context-menu';

type UseEmbryoContextMenuProps = {
  selectedEmbryos: ShipmentEmbryo[];
  onRemoveFromShipment: (embryos: ShipmentEmbryo[]) => void;
  canExecuteWorkflow: boolean;
  isRemoveFromShipmentDisabled: boolean;
};

export const useEmbryoContextMenu = ({
  selectedEmbryos,
  onRemoveFromShipment,
  canExecuteWorkflow,
  isRemoveFromShipmentDisabled,
}: UseEmbryoContextMenuProps) => {
  const getContextMenuItems = useCallback<GetContextMenuItems>(
    ({ node, api }) => {
      const selectedCount = selectedEmbryos.length;
      const hasSelection = selectedCount > 0;

      const items: (string | MenuItemDef)[] = [];

      const embryoId: string | undefined = node?.data?.id;
      if (embryoId) {
        items.push(auditHistoryMenuItem({ rowId: embryoId }));
      }

      if (hasSelection) {
        if (items.length > 0) {
          items.push('separator');
        }

        items.push({
          name: 'Workflows',
          icon: '<i class="pi pi-wrench"></i>',
          disabled: !canExecuteWorkflow || isRemoveFromShipmentDisabled,
          subMenu: [
            {
              name: 'Remove from Shipment',
              icon: '<i class="pi pi-play-circle"></i>',
              disabled: isRemoveFromShipmentDisabled,
              action: () => onRemoveFromShipment(selectedEmbryos),
            },
          ],
        });
      }

      if (items.length > 0) {
        items.push('separator');
      }

      items.push(...DEFAULT_CONTEXT_MENU);

      return replaceExportItem(items, api);
    },
    [
      selectedEmbryos,
      onRemoveFromShipment,
      canExecuteWorkflow,
      isRemoveFromShipmentDisabled,
    ],
  );

  return { getContextMenuItems };
};
